/**
 * inviteDetection — captures invitation-link arrivals.
 *
 * Two invitation link shapes exist:
 *   - App-owned invites: /welcome?invite_token=<raw>. The token stays in the
 *     URL until it is redeemed; it is NEVER written to storage. Only a
 *     type marker is persisted.
 *   - Legacy Supabase OTP invites (token_hash / type=invite), which Supabase
 *     JS may strip from the URL during client init, so the marker is
 *     captured synchronously at module load.
 *
 * Password-recovery links (type=recovery) are NOT invite arrivals: they land
 * on /reset-password, which handles them itself. Treating them as invites
 * would send already-registered users with onboarding_complete=false into
 * the onboarding form.
 */
const INVITE_KEY = 'cv80_invite_arrival';
const RECOVERY_KEY = 'cv80_recovery_arrival';
const INVITE_TOKEN_PARAM = 'invite_token';
const RESET_PASSWORD_PATH = '/reset-password';

const readParams = (value = '') => new URLSearchParams(value.replace(/^#/, ''));

const readStoredArrival = () => {
  const stored = sessionStorage.getItem(INVITE_KEY);
  if (!stored) return null;
  if (stored === '1') return { type: 'invite' };

  try {
    return JSON.parse(stored);
  } catch (e) {
    return { type: 'invite' };
  }
};

try {
  if (typeof window !== 'undefined' && window.location.pathname !== RESET_PASSWORD_PATH) {
    const searchParams = readParams(window.location.search);
    const hashParams = readParams(window.location.hash);
    const type = searchParams.get('type') || hashParams.get('type');
    const hasInviteToken = Boolean(searchParams.get(INVITE_TOKEN_PARAM));
    const errorCode = searchParams.get('error_code') || hashParams.get('error_code');
    const errorDescription =
      searchParams.get('error_description') || hashParams.get('error_description');
    // App-token arrivals are detected from the URL (getInviteTokenFromUrl) and
    // deliberately NOT persisted: the stored marker makes PortalRouter send
    // any signed-in user with onboarding_complete !== true to /welcome, which
    // would misroute an existing user who opens someone else's invite link.
    const isInviteArrival =
      !hasInviteToken &&
      (searchParams.get('onboarding') === '1' ||
      ['invite', 'signup', 'magiclink', 'email'].includes(type) ||
      (type !== 'recovery' && Boolean(errorCode || errorDescription)));

    if (isInviteArrival) {
      // Never persist invite_token, token_hash, access_token, or refresh_token.
      // The marker retains only enough context to explain a failed link.
      sessionStorage.setItem(
        INVITE_KEY,
        JSON.stringify({
          type: type || 'invite',
          errorCode: errorCode || '',
          errorDescription: errorDescription || '',
          capturedAt: Date.now(),
        })
      );
    }
  }
} catch (e) { /* sessionStorage unavailable — treat as normal arrival */ }

// Password-recovery arrival (boolean only — tokens are never stored). If a
// recovery email lands anywhere other than /reset-password (for example the
// site root, when the redirect URL is not allow-listed), the app forwards the
// now-signed-in recovery session to /reset-password instead of silently
// logging the person in with no way to set a new password.
try {
  if (typeof window !== 'undefined') {
    const searchParams = readParams(window.location.search);
    const hashParams = readParams(window.location.hash);
    const type = searchParams.get('type') || hashParams.get('type');
    if (type === 'recovery') sessionStorage.setItem(RECOVERY_KEY, '1');
  }
} catch (e) { /* no-op */ }

export const arrivedViaRecovery = () => {
  try {
    return sessionStorage.getItem(RECOVERY_KEY) === '1';
  } catch (e) {
    return false;
  }
};

export const clearRecoveryArrival = () => {
  try {
    sessionStorage.removeItem(RECOVERY_KEY);
  } catch (e) { /* no-op */ }
};

/** Returns the app-owned invite token from the current URL, or ''. */
export const getInviteTokenFromUrl = () => {
  try {
    if (typeof window === 'undefined') return '';
    return new URLSearchParams(window.location.search).get(INVITE_TOKEN_PARAM) || '';
  } catch (e) {
    return '';
  }
};

/** True when the current URL is an app-owned invite link. */
export const arrivedViaInviteToken = () => Boolean(getInviteTokenFromUrl());

/** Removes the invite token from the address bar (after successful redemption). */
export const removeInviteTokenFromUrl = () => {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete(INVITE_TOKEN_PARAM);
    window.history.replaceState({}, document.title, `${url.pathname}${url.search}`);
  } catch (e) { /* no-op */ }
};

export const arrivedViaInvite = () => {
  try {
    return Boolean(readStoredArrival());
  } catch (e) {
    return false;
  }
};

export const getInviteArrival = () => {
  try {
    return readStoredArrival();
  } catch (e) {
    return null;
  }
};

export const clearInviteArrival = () => {
  try {
    sessionStorage.removeItem(INVITE_KEY);
  } catch (e) { /* no-op */ }
};
